import React from 'react';

/**
 * Spec 28 §7.5 (stream abill): the minimal Markdown of client updates — paragraphs, "- " / "* " / "1. " lists, **bold**
 * and http(s) links (rel="noopener noreferrer nofollow") — rendered as React elements. No HTML string is ever injected,
 * so markup in the text is shown as text.
 */

const LINK = /\[([^\]]{1,200})\]\((https?:\/\/[^\s)]{1,500})\)|(https?:\/\/[^\s<)]{1,500})|\*\*([^*]+)\*\*/g;

export function renderInline(text: string, keyPrefix = 'i'): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let index = 0;
  let match: RegExpExecArray | null;
  LINK.lastIndex = 0;
  while ((match = LINK.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${index++}`;
    if (match[4] !== undefined) nodes.push(<strong key={key}>{match[4]}</strong>);
    else {
      const href = match[2] ?? match[3];
      nodes.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow">
          {match[1] ?? href}
        </a>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function renderUpdateBody(body: string): React.ReactNode[] {
  const blocks = body.replace(/\r\n/g, '\n').split(/\n{2,}/);
  return blocks.map((block, blockIndex) => {
    const lines = block.split('\n').filter(line => line.trim());
    const key = `b${blockIndex}`;
    if (!lines.length) return null;
    if (lines.every(line => /^\s*[-*]\s+/.test(line))) {
      return (
        <ul key={key}>
          {lines.map((line, i) => (
            <li key={i}>{renderInline(line.replace(/^\s*[-*]\s+/, ''), `${key}-${i}`)}</li>
          ))}
        </ul>
      );
    }
    if (lines.every(line => /^\s*\d+[.)]\s+/.test(line))) {
      return (
        <ol key={key}>
          {lines.map((line, i) => (
            <li key={i}>{renderInline(line.replace(/^\s*\d+[.)]\s+/, ''), `${key}-${i}`)}</li>
          ))}
        </ol>
      );
    }
    return (
      <p key={key}>
        {lines.map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {renderInline(line, `${key}-${i}`)}
          </React.Fragment>
        ))}
      </p>
    );
  });
}
